import { Button } from "design-system/atoms/Button"
import {
  Dialog as DesignDialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "design-system/molecules/DropdownMenu"
import { Ellipsis } from "lucide-react"
import { useCancelInvitationMutation } from "modules/settings/hooks/useCancelInvitationMutation"
import { useResendInvitationMutation } from "modules/settings/hooks/useResendInvitationMutation"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { EmployeeInviteResponseData } from "schema"
import { toast } from "sonner"

interface Props {
  invitation: EmployeeInviteResponseData
}

export function InvitationActions({ invitation }: Props) {
  const { t } = useTranslation()
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const { mutateAsync: onResend, isPending: isResendPending } = useResendInvitationMutation()
  const { mutateAsync: onCancel, isPending: isCancelPending } = useCancelInvitationMutation()

  const isPending = isResendPending || isCancelPending

  async function handleResend() {
    await onResend({ id: invitation.id })
    toast.success(t("settings.team.table.resendSuccess"))
  }

  async function handleCancel() {
    await onCancel({ id: invitation.id })
    toast.success(t("settings.team.table.cancelSuccess"))
    setIsDialogOpen(false)
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" disabled={isPending}>
            <Ellipsis />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onClick={handleResend}>
            {t("settings.team.table.actions.resendInvite")}
          </DropdownMenuItem>
          <DropdownMenuItem className="text-destructive" onClick={() => setIsDialogOpen(true)}>
            {t("settings.team.table.actions.cancelInvitation")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DesignDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("settings.team.table.cancelInvitationDialog.title")}</DialogTitle>
            <DialogDescription>
              {t("settings.team.table.cancelInvitationDialog.description", {
                name: invitation.name,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setIsDialogOpen(false)}>
              {t("shared.cancel")}
            </Button>
            <Button variant="destructive" onClick={handleCancel} disabled={isCancelPending}>
              {t("settings.team.table.cancelInvitationDialog.cta")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DesignDialog>
    </>
  )
}
